import type { BookingResponse } from '@/shared/api';
import type { InvoiceStepState } from './bookingInvoiceModel';

export type ProgressState =
  | 'completed'
  /** Erreicht, aber nicht erledigt – z. B. eine Rechnung, die noch nicht bezahlt ist. */
  | 'open'
  | 'current'
  | 'future'
  | 'cancelled'
  | 'skipped'
  | 'unavailable';

export interface ProgressStep {
  id: string;
  label: string;
  state: ProgressState;
}

export interface BuildProgressInput {
  booking: Pick<BookingResponse, 'status' | 'stayPhase'>;
  onboardingComplete: boolean | null;
  /** `null`: für die Rolle nicht sichtbar oder noch nicht geladen. */
  invoiceState: InvoiceStepState | null;
}

// Leitet die 4 Stufen aus Status, serverseitiger Aufenthaltsphase und verfügbaren
// Onboarding-/Rechnungswahrheiten ab. Cancelled/NoShow bricht den ersten offenen Schritt ab.
export function buildProgress({
  booking,
  onboardingComplete,
  invoiceState,
}: BuildProgressInput): ProgressStep[] {
  const { status } = booking;
  const atLeastConfirmed =
    status === 'Confirmed' || status === 'CheckedIn' || status === 'CheckedOut';
  const stayComplete = booking.stayPhase === 'Completed';
  const staying = ['Arrival', 'InHouse', 'Departure'].includes(booking.stayPhase ?? '');

  const steps: ProgressStep[] = [
    { id: 'confirmed', label: 'Bestätigt', state: atLeastConfirmed ? 'completed' : 'current' },
    {
      id: 'onboarding',
      label: 'Onboarding',
      state:
        onboardingComplete === null
          ? 'unavailable'
          : onboardingComplete
            ? 'completed'
            : !atLeastConfirmed
              ? 'future'
              : // Ab dem Aufenthalt ist der Aufenthalt der aktuelle Schritt; offenes Onboarding bleibt Bernstein.
                staying || stayComplete
                ? 'open'
                : 'current',
    },
    {
      id: 'stay',
      label: 'Aufenthalt',
      state: stayComplete ? 'completed' : staying ? 'current' : 'future',
    },
    {
      id: 'invoice',
      label: 'Rechnung',
      // Grün erst, wenn alles bezahlt ist; eine offene Rechnung bleibt sichtbar offen.
      state:
        invoiceState === null
          ? 'unavailable'
          : invoiceState === 'paid'
            ? 'completed'
            : invoiceState === 'open'
              ? 'open'
              : 'future',
    },
  ];

  if (status === 'Cancelled' || status === 'NoShow') {
    const label = status === 'Cancelled' ? 'Storniert' : 'No-Show';
    const firstOpen = steps.findIndex((s) => s.state !== 'completed');
    return steps.map((s, i) => {
      if (i === firstOpen) return { ...s, label, state: 'cancelled' as ProgressState };
      if (firstOpen >= 0 && i > firstOpen) return { ...s, state: 'skipped' as ProgressState };
      return s;
    });
  }

  return steps;
}
